import { useEffect, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import { t } from '@/lib/i18n'
import { buzz, coin, fanfare, scheduleTicks, tick } from '@/lib/sound'

// 停下前转满 5 圈：少于 3 圈看着像卡顿，多于 6 圈开始让人不耐烦。
const TURNS = 5
const SPIN_MS = 4000
// 转盘的分类调色盘，值定义在 css/application.css 的 :root 里（不在 @theme 中，
// 因为它们只被这里用 var() 读，不需要生成 utility）。装饰性的，别当语义色用。
const SLOT_FILL = [1, 2, 3, 4, 5, 6].map((i) => `var(--wheel-${i})`)

// 盘画在 100×100 的 viewBox 里，角度以 12 点为 0°、顺时针为正。
// 指针固定在 12 点，所以「把第 i 格转到指针下」就是把整个盘反向转 slotCenter(i)。
const R = 46
const C = 50

const slotCenter = (i: number, n: number) => (i + 0.5) * (360 / n)

function polar(angle: number, radius: number): [number, number] {
  const rad = ((angle - 90) * Math.PI) / 180
  return [C + radius * Math.cos(rad), C + radius * Math.sin(rad)]
}

function wedge(i: number, n: number): string {
  const step = 360 / n
  const [x1, y1] = polar(i * step, R)
  const [x2, y2] = polar((i + 1) * step, R)
  return `M ${C} ${C} L ${x1} ${y1} A ${R} ${R} 0 ${step > 180 ? 1 : 0} 1 ${x2} ${y2} Z`
}

/**
 * 打卡转盘：每天打过卡就有一次。
 *
 * 两个信号、两条生命周期：
 *   * flash.celebrate.spin —— 刚打完卡，自动弹一次（一次性）
 *   * props.spin.available —— 今天还没转，右下角留一个带红点的入口（常驻）
 *
 * 盘面在**打开的那一刻**快照进 state：转完之后 props.spin 会翻成不可转、slots 变空，
 * 而那正是动画要开始的时刻——直接读 props 会让盘在转的过程中被清空。
 */
export function SpinWheel() {
  const { flash, spin } = usePage<SharedProps>().props
  const result = flash?.spin_result ?? null
  const available = spin?.available ?? false
  // cards（目标达成 / 徽章解锁）非空时，转盘的**自动弹**要让路（见下面的 Effect A）。
  // 这跟 Celebration.TOAST_TYPES 早定下的分层是同一条原则——稀罕的事才值得打断用户：
  // 目标达成 / 徽章解锁比每天都有的转盘稀罕，这一刻由它独占。
  //
  // 右下角那个手动徽标**不**用这个标志来隐藏，靠的是层级（见下面的 z-35）。
  // 理由：hasCards 跟踪的是「这份 flash 里有没有卡」，不是「卡有没有被关掉」，
  // 拿它挡徽标的话，孩子关掉成就卡之后徽标要等到下一次整页导航才回来。
  const hasCards = (flash?.celebrate?.cards?.length ?? 0) > 0

  const [open, setOpen] = useState(false)
  const [slots, setSlots] = useState<number[]>([])
  const [angle, setAngle] = useState(0)
  const [spinning, setSpinning] = useState(false)
  const [landed, setLanded] = useState<number | null>(null)
  // 从点击「转！」到服务端响应回来之间的窗口——spinning 要等 Effect B 收到
  // result 才会变 true，双击就在这段窗口里溜过 spinning 那道防重入检查（Finding 3）。
  const [submitting, setSubmitting] = useState(false)

  const openWheel = () => {
    setSlots(spin?.slots ?? [])
    setAngle(0)
    setLanded(null)
    setSpinning(false)
    setSubmitting(false)
    setOpen(true)
  }

  // 打完卡自动弹。celebrate 是一次性的 flash，所以只跟着它变化触发；
  // 依赖里不放 available——它在转完后会变，会把刚关掉的弹窗又弹回来。
  useEffect(() => {
    if (flash?.celebrate?.spin && !hasCards) openWheel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash?.celebrate])

  // 结果到了就转。角度是「转满 5 圈，再倒回去让第 index 格对准指针」。
  //
  // 咔哒声是这个功能的核心：大脑在声音变慢的过程里预测结果，心跳来自那段过程，
  // 不是来自最后那个数字。
  //
  // 必须同时要求弹窗是开着的。Inertia 会把 props（含 flash）缓存进 history.state：
  // 转完之后走到别的页面再按后退键，spin_result 原样复原、这个 Effect 再跑一遍，
  // 于是 36 声咔哒加落地音在没有任何盘面的页面上响一遍。open 不放进依赖数组——
  // 该跟着变化的只有 result；一次成功的转盘之后 available 已经是 false、徽标不再
  // 出现，所以不存在「带着旧 result 重新打开」这条路径。
  useEffect(() => {
    if (!open || !result) return
    const n = slots.length || 6
    setSpinning(true)
    setLanded(null)
    setAngle(360 * TURNS - slotCenter(result.index, n))

    const timers = scheduleTicks(SPIN_MS).map((at) => window.setTimeout(tick, at))
    timers.push(
      window.setTimeout(() => {
        setSpinning(false)
        setLanded(result.delta)
        // 最大的那一格值得一段琶音；其余是金币落袋。失败与撤销一律无声——
        // 这个产品不惩罚，出错的孩子不该被 App 用声音指出来。
        if (result.index === n - 1) {
          fanfare()
          buzz(200)
        } else {
          coin()
          buzz(20)
        }
      }, SPIN_MS),
    )
    return () => timers.forEach(window.clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  // preserveState：不重建组件，否则动画状态在响应回来的那一刻就被重置了。
  const doSpin = () => {
    if (spinning || submitting || landed !== null) return
    setSubmitting(true)
    router.post(
      '/spin',
      {},
      {
        preserveState: true,
        preserveScroll: true,
        // 不管成功、失败还是被取消都要把提交中标志放下来——失败时（例如真的撞上
        // 「今天已经转过了」）没有 spin_result，Effect B 不会跑，spinning 也就
        // 永远不会变 true，提交中标志若不在这里兜底就会把按钮锁死。
        onFinish: () => setSubmitting(false),
      },
    )
  }

  // 转动中（spinning）或请求还在飞行中（submitting）都不放行：前者关掉会看不到
  // 结果，后者关掉会让人以为「还没转」——其实请求已经在路上，马上就要把
  // available 变成 false。三条退出路径（遮罩 / 按钮 / Escape）都读同一个 busy。
  const busy = spinning || submitting

  const requestClose = () => {
    if (busy) return
    setOpen(false)
  }

  // 弹窗开着时才监听 Escape，关掉后立刻解绑，不留一个全局 keydown 处理器。
  // 依赖里带 busy：一旦点了「转！」就要重新订阅一次，让处理函数读到最新的
  // busy，转动 / 提交中 Escape 才会正确失效。
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, busy])

  if (!open) {
    if (!available) return null
    // z-35 是**刻意低于** CelebrationCard 那张 z-80 的全屏遮罩：成就卡在的时候，
    // 徽标被它盖住，既看不见也点不到，于是孩子不可能把转盘叠到成就卡上面；卡片一被
    // 关掉就整个卸载，徽标同一帧回来，不需要任何跨组件状态去描述「卡还在不在」。
    //
    // 位置不能是 bottom-5：底部 56px + 安全区已经归底部 tab bar 了，转盘会被压在
    // 下面。above-tabbar 这个 utility 就是那个 calc（见 css/application.css）。
    // 同理 z 层也在 tab bar（40）之下 —— 它本来就浮在 tab bar 上方，不需要盖住它。
    return (
      <button
        type="button"
        onClick={openWheel}
        title={t('spin.title')}
        className="fixed right-5 above-tabbar z-[35] w-14 h-14 rounded-full bg-card border border-line shadow-lg text-2xl grid place-items-center hover:border-brand"
      >
        🎰
        <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-red border-2 border-card" />
      </button>
    )
  }

  const n = slots.length || 6

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/40 px-4"
      onClick={(e) => {
        // 只认「直接点在遮罩本身」——点白色卡片会先命中里面的子元素，事件虽然
        // 冒泡到这里，但 e.target 仍是那个子元素、不等于 currentTarget（遮罩自己），
        // 所以点卡片内部不会触发关闭。
        if (e.target === e.currentTarget) requestClose()
      }}
    >
      <div className="bg-card rounded-2xl shadow-xl p-6 w-full max-w-[340px] text-center">
        <div className="text-lg font-extrabold mb-1">{t('spin.title')}</div>
        <div className="text-[13px] text-muted mb-4">{t('spin.hint')}</div>

        <div className="relative w-[240px] h-[240px] mx-auto">
          {/* 指针固定在 12 点，转的是盘 */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10 text-2xl leading-none">🔻</div>
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full drop-shadow"
            style={{
              transform: `rotate(${angle}deg)`,
              transition: spinning ? `transform ${SPIN_MS}ms cubic-bezier(.15,.9,.25,1)` : 'none',
            }}
          >
            {slots.map((points, i) => {
              const [lx, ly] = polar(slotCenter(i, n), 30)
              return (
                <g key={i}>
                  <path d={wedge(i, n)} fill={SLOT_FILL[i % SLOT_FILL.length]} />
                  <text
                    x={lx}
                    y={ly}
                    fill="#fff"
                    fontSize="9"
                    fontWeight="800"
                    textAnchor="middle"
                    dominantBaseline="central"
                    transform={`rotate(${slotCenter(i, n)} ${lx} ${ly})`}
                  >
                    +{points}
                  </text>
                </g>
              )
            })}
            <circle cx={C} cy={C} r="8" fill="#fff" />
          </svg>
        </div>

        {landed === null ? (
          <>
            <button
              type="button"
              onClick={doSpin}
              disabled={busy}
              className="mt-5 w-full py-3 rounded-xl bg-brand text-white font-extrabold disabled:opacity-60"
            >
              {busy ? t('spin.spinning') : t('spin.cta')}
            </button>
            {/* 混合形态的另一半：不转也能关——关掉后徽标还在，一天内随时能回来转。
                不能只靠点遮罩关：小孩不知道遮罩能点，必须有一个写着字的按钮。 */}
            <button
              type="button"
              onClick={requestClose}
              disabled={busy}
              className="mt-2 w-full py-2.5 rounded-xl text-muted font-semibold disabled:opacity-60"
            >
              {t('spin.later')}
            </button>
          </>
        ) : (
          <>
            <div className="mt-5 text-xl font-extrabold text-brand">
              {t('spin.result', { delta: landed })}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-3 w-full py-3 rounded-xl bg-tint text-brand font-extrabold"
            >
              {t('spin.close')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
